import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { useState, useRef, useMemo, useEffect } from 'react';
import { useTranslation } from '../../i18n';
import { DailyTask, CategorizedTasks } from '../../hooks/useDailyRoutines';
import { Check } from 'phosphor-react';
import { TaskDetailSheet } from './TaskDetailSheet';
import {
  trackTaskCompleted,
  mapTaskType,
  mapTimeCategoryToTime,
  mapContentCategoryToContentType,
  type TaskProps,
} from '../../services/analytics.service';

function buildWelcomeTaskProps(task: DailyTask): TaskProps {
  return {
    type: mapTaskType({
      type: task.type,
      deeplink: task.deeplink,
      contentCategory: (task as any).contentCategory,
    }),
    contentType: mapContentCategoryToContentType(
      (task as any).contentCategory || task.contentType
    ),
    contentID: (task as any).contentId || (task as any).content_id || null,
    taskTitle: task.title || '',
    time: mapTimeCategoryToTime(task.timeCategory),
  };
}

interface AgentOption {
  id: string;
  label: string;
  labelTr: string;
  icon: string;
  glowColor: string;
  glowPosition: string;
}

const AGENT_OPTIONS: AgentOption[] = [
  { id: 'mental-health', label: 'Mental Health', labelTr: 'Mental Sağlık', icon: '/img/assistant-icons-mental-health.png', glowColor: 'rgba(165,100,208,0.35)', glowPosition: 'right -10px top -20px' },
  // { id: 'nutrition', label: 'Nutrition', labelTr: 'Beslenme', icon: '/img/assistant-icons-nutrition.png', glowColor: 'rgba(238,160,4,0.3)', glowPosition: 'right -5px top -35px' },
  { id: 'content-recommendation', label: 'Content Suggestion', labelTr: 'İçerik Önerisi', icon: '/img/assistant-icons-content-recomendation.png', glowColor: 'rgba(39,114,230,0.3)', glowPosition: 'right -15px top -10px' },
  { id: 'faq', label: 'Support', labelTr: 'Destek & Genel', icon: '/img/assistant-icons-faq.png', glowColor: 'rgba(70,175,44,0.3)', glowPosition: 'right -10px top -40px' },
];

interface WelcomeScreenProps {
  userName?: string;
  isLoading?: boolean;
  routinesLoading?: boolean;
  categorizedRoutines?: { [date: string]: CategorizedTasks };
  onCompleteTask?: (date: string, taskId: string) => void;
  token?: string;
  onAgentSelect?: (agentId: string) => void;
}

const CONTENT_TYPE_I18N_MAP: Record<string, string> = {
  meditation: 'content.meditation',
  story: 'content.story',
  music: 'content.music',
  blog: 'content.article',
  talk: 'content.article',
  yoga: 'content.yoga',
  theme: 'content.theme',
  playlist: 'content.playlist',
  category: 'content.category',
};

const TIME_PERIODS = [
  { key: 'sabah' as const, labelKey: 'routines.morning', emoji: '\u2600\uFE0E' },
  { key: 'ogle' as const, labelKey: 'routines.afternoon', emoji: '\u26C5' },
  { key: 'aksam' as const, labelKey: 'routines.evening', emoji: '\uD83C\uDF19' },
] as const;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  flex: 1;
  min-height: 0;
  width: 100%;
  max-width: 375px;
  margin: 0 auto;
  padding: 64px 14px 0;
  position: relative;
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar { display: none; }
`;

const breathe = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
`;

const LogoContainer = styled.div`
  width: 36px;
  height: 37px;
  margin-bottom: 16px;
  position: relative;
  flex-shrink: 0;
  animation: ${breathe} 4s ease-in-out infinite;

  svg {
    width: 100%;
    height: 100%;
  }

  @keyframes shimmerMove {
    0% {
      x: -200%;
    }
    100% {
      x: 200%;
    }
  }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const WelcomeText = styled.div<{ $isVisible: boolean }>`
  font-family: 'Playfair Display', serif;
  font-weight: 500;
  font-size: 24px;
  line-height: 28px;
  color: var(--color-text);
  text-align: left;
  max-width: 299px;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  animation: ${({ $isVisible }) => ($isVisible ? fadeInUp : 'none')} 0.8s ease-out;
  transition: opacity 0.3s ease-out;
  flex-shrink: 0;

  p {
    margin: 0;
  }

  p:first-of-type {
    margin-bottom: 4px;
  }

  p:last-of-type {
    font-size: 20px;
    line-height: 24px;
  }
`;

const TaskSummaryWrapper = styled.div`
  position: relative;
  padding-top: 20px;
  flex-shrink: 0;
  opacity: 0;
  animation: ${fadeInUp} 0.6s ease-out 0.75s forwards;
`;

const TaskSummaryLayer = styled.p<{ $active: boolean }>`
  font-family: 'Inter', 'Hanken Sans', sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 28px;
  color: var(--color-text-secondary);
  margin: 0;
  text-align: left;
  opacity: ${({ $active }) => ($active ? 1 : 0)};
  transition: opacity 0.4s ease-in-out;

  strong {
    color: var(--color-primary);
    font-weight: 600;
  }
`;

const TaskSection = styled.div`
  width: 100%;
  min-height: 240px;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  margin-top: 4px;
  opacity: 0;
  animation: ${fadeInUp} 0.6s ease-out 0.85s forwards;
`;

const CarouselWrapper = styled.div`
  width: calc(100% + 28px);
  margin-left: -14px;
  margin-top: 16px;
  position: relative;
  flex-shrink: 0;
  overflow: hidden;

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    width: 32px;
    z-index: 1;
    pointer-events: none;
  }

  &::before {
    left: 0;
    background: linear-gradient(to right, var(--color-bg), transparent);
  }

  &::after {
    right: 0;
    background: linear-gradient(to left, var(--color-bg), transparent);
  }
`;

const CarouselTrack = styled.div<{ $offset: number }>`
  display: flex;
  gap: 8px;
  padding: 0 14px;
  transform: translateX(${({ $offset }) => $offset}px);
  transition: transform 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  will-change: transform;

  &[data-dragging="true"] {
    transition: none;
  }
`;

const TaskCard = styled.div`
  flex-shrink: 0;
  width: calc(100% - 28px);
  background: var(--color-card-bg);
  border: 1px solid var(--color-border-subtle);
  border-radius: 16px;
  padding: 20px;
`;

const CardHeader = styled.div`
  font-family: 'Inter', 'Hanken Sans', sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 28px;
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 16px;
`;

const TaskList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;


const TaskRow = styled.div<{ $completed: boolean; $index: number; $active: boolean; $baseDelay: number }>`
  display: flex;
  align-items: flex-start;
  gap: 6px;

  ${({ $active, $completed, $index, $baseDelay }) => $active ? `
    opacity: 0;
    animation: ${$completed ? 'taskFadeInCompleted' : 'taskFadeInNormal'} 0.25s ease-out forwards;
    animation-delay: ${$baseDelay + $index * 60}ms;

    @keyframes taskFadeInNormal {
      from { opacity: 0; transform: translateY(8px); }
      to { opacity: 1; transform: translateY(0); }
    }

    @keyframes taskFadeInCompleted {
      from { opacity: 0; transform: translateY(8px); }
      to { opacity: 0.7; transform: translateY(0); }
    }
  ` : `
    opacity: ${$completed ? 0.7 : 1};
  `}
`;

const Checkbox = styled.div<{ $checked: boolean }>`
  width: 18px;
  height: 18px;
  border-radius: 5.4px;
  border: 0.9px solid ${({ $checked }) => ($checked ? '#C19FEC' : 'var(--color-text-secondary)')};
  background: ${({ $checked }) => ($checked ? '#C19FEC' : 'transparent')};
  flex-shrink: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1px;
  transition: all 0.2s ease;
`;

const TaskInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
`;

const TaskTitle = styled.span<{ $completed: boolean }>`
  font-family: 'Inter', 'Hanken Sans', sans-serif;
  font-weight: 500;
  font-size: 13px;
  line-height: 16px;
  color: var(--color-text);
  text-decoration: ${({ $completed }) => ($completed ? 'line-through' : 'none')};
`;

const TaskMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ContentTitle = styled.span`
  font-family: 'Inter', 'Hanken Sans', sans-serif;
  font-weight: 400;
  font-size: 11px;
  line-height: 16px;
  color: var(--color-primary);
`;

const ContentTypeBadge = styled.span`
  font-family: 'Inter', 'Hanken Sans', sans-serif;
  font-weight: 400;
  font-size: 9px;
  line-height: 12px;
  color: var(--color-text-secondary);
  text-transform: uppercase;
  text-align: center;
  background: var(--color-border-subtle);
  border-radius: 54px;
  padding: 2px 6px;
`;

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const SkeletonBase = styled.div`
  background: linear-gradient(
    90deg,
    var(--color-bg-elevated) 25%,
    var(--color-border-subtle) 50%,
    var(--color-bg-elevated) 75%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.6s infinite;
  border-radius: 6px;
`;

const SkeletonCard = styled.div`
  flex-shrink: 0;
  width: calc(100% - 28px);
  background: var(--color-bg);
  border: 1px solid var(--color-border-subtle);
  border-radius: 16px;
  padding: 20px;
`;

const SkeletonLine = styled(SkeletonBase)<{ $width?: string; $height?: string }>`
  width: ${({ $width }) => $width || '100%'};
  height: ${({ $height }) => $height || '13px'};
`;

const SkeletonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const DotsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 6px;
  margin-top: 16px;
  padding-bottom: 40px;
`;

const Dot = styled.div<{ $active: boolean }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ $active }) => ($active ? 'var(--color-text)' : 'var(--color-border)')};
  transition: background 0.2s ease;
`;

const AgentsSection = styled.div`
  width: calc(100% + 28px);
  margin-left: -14px;
  margin-top: 12px;
  padding-bottom: 8px;
  flex-shrink: 0;
  position: relative;
  overflow: hidden;

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    width: 32px;
    z-index: 1;
    pointer-events: none;
  }

  &::before {
    left: 0;
    background: linear-gradient(to right, var(--color-bg), transparent);
  }

  &::after {
    right: 0;
    background: linear-gradient(to left, var(--color-bg), transparent);
  }
`;

const AgentsSectionTitle = styled.h3`
  font-family: 'Inter', 'Hanken Sans', sans-serif;
  font-weight: 600;
  font-size: 16px;
  color: var(--color-text-secondary);
  margin: 0 0 12px 14px;
  padding-top: 40px;
  opacity: 0;
  animation: ${fadeInUp} 0.6s ease-out 0.3s forwards;
`;

const AgentsScroll = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding: 0 14px 4px;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar { display: none; }
`;

const AgentCard = styled.button<{ $index: number }>`
  flex-shrink: 0;
  width: 149px;
  height: 99px;
  border-radius: 16px;
  overflow: hidden;
  position: relative;
  cursor: pointer;
  transition: transform 0.15s ease;
  padding: 0;
  isolation: isolate;
  -webkit-mask-image: -webkit-radial-gradient(white, black);
  opacity: 0;
  animation: ${fadeInUp} 0.4s ease-out forwards;
  animation-delay: ${({ $index }) => 0.4 + $index * 0.08}s;

  background: #FFFFFF;
  border: 1px solid #FFFFFF;
  box-shadow: 0px 4px 7px 0px #e2e4e8;

  [data-theme="dark"] & {
    background: rgba(0, 0, 0, 0.32);
    border: 1px solid rgba(255, 255, 255, 0.16);
    box-shadow: none;
  }

  &:active {
    transform: scale(0.96);
  }
`;

const AgentCardGlow = styled.div<{ $color: string; $top: string; $right: string }>`
  position: absolute;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
  filter: blur(20px);
  top: ${({ $top }) => $top};
  right: ${({ $right }) => $right};
  pointer-events: none;
`;

const AgentIcon = styled.img`
  position: absolute;
  left: 6px;
  top: 9px;
  width: 56px;
  height: 56px;
  object-fit: contain;
`;

const AgentLabel = styled.span`
  position: absolute;
  left: 11px;
  top: 66px;
  font-family: 'Hanken Sans', 'Inter', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 16px;
  white-space: nowrap;
  color: var(--color-text);

  [data-theme="dark"] & {
    color: #FFFFFF;
  }
`;

function getCurrentTimePeriodKey(): 'sabah' | 'ogle' | 'aksam' {
  const hour = new Date().getHours();
  if (hour < 12) return 'sabah';
  if (hour < 17) return 'ogle';
  return 'aksam';
}

function getTodayDateStr(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export const WelcomeScreen: React.FC<WelcomeScreenProps> = ({
  userName = 'User',
  isLoading = false,
  routinesLoading = false,
  categorizedRoutines,
  onCompleteTask,
  token,
  onAgentSelect,
}) => {
  const { t, lang } = useTranslation();
  const trackRef = useRef<HTMLDivElement>(null);
  const currentPeriod = getCurrentTimePeriodKey();
  const today = getTodayDateStr();
  const todayCategorized = categorizedRoutines?.[today];

  // Build carousel slides: morning, afternoon, evening (only non-empty)
  const slides = useMemo(() => {
    if (!todayCategorized) return [];
    return TIME_PERIODS
      .map((period) => ({
        ...period,
        tasks: (todayCategorized[period.key] as DailyTask[]) || [],
      }))
      .filter((slide) => slide.tasks.length > 0);
  }, [todayCategorized]);

  // Start at the current time period slide
  const initialSlideIndex = useMemo(() => {
    const idx = slides.findIndex((s) => s.key === currentPeriod);
    return idx >= 0 ? idx : 0;
  }, [slides, currentPeriod]);

  const [activeSlide, setActiveSlide] = useState(initialSlideIndex);
  const [animationKey, setAnimationKey] = useState(0);
  const [baseDelay, setBaseDelay] = useState(0);
  const hasSetInitialSlide = useRef(false);

  // Sync to correct slide once slides load (async routines)
  useEffect(() => {
    if (slides.length > 0 && !hasSetInitialSlide.current) {
      hasSetInitialSlide.current = true;
      setActiveSlide(initialSlideIndex);
      // Wait for carousel transition (350ms) before animating tasks
      setBaseDelay(initialSlideIndex > 0 ? 380 : 0);
      setAnimationKey(k => k + 1);
    }
  }, [slides.length, initialSlideIndex]);

  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef<{ x: number; time: number } | null>(null);
  const [selectedTask, setSelectedTask] = useState<DailyTask | null>(null);

  // Calculate card width + gap
  const getCardStep = (): number => {
    if (!trackRef.current?.children[0]) return 0;
    return (trackRef.current.children[0] as HTMLElement).offsetWidth + 8; // card width + gap
  };

  const goToSlide = (index: number) => {
    const clamped = Math.max(0, Math.min(index, slides.length - 1));
    setActiveSlide(clamped);
    setDragOffset(0);
    setBaseDelay(380); // wait for carousel transition
    setAnimationKey(k => k + 1);
  };

  // Touch handlers for pager swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    dragStart.current = { x: e.touches[0].clientX, time: Date.now() };
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!dragStart.current) return;
    const dx = e.touches[0].clientX - dragStart.current.x;
    setDragOffset(dx);
  };

  const handleTouchEnd = () => {
    if (!dragStart.current) return;
    const step = getCardStep();
    const elapsed = Date.now() - dragStart.current.time;
    const velocity = Math.abs(dragOffset) / elapsed;

    // Swipe if dragged > 25% of card or fast flick
    if (Math.abs(dragOffset) > step * 0.25 || velocity > 0.3) {
      if (dragOffset < 0) goToSlide(activeSlide + 1);
      else goToSlide(activeSlide - 1);
    } else {
      setDragOffset(0);
    }
    dragStart.current = null;
    setIsDragging(false);
  };

  // Mouse handlers for desktop pager drag
  const handleMouseDown = (e: React.MouseEvent) => {
    dragStart.current = { x: e.clientX, time: Date.now() };
    setIsDragging(true);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!dragStart.current) return;
    const dx = e.clientX - dragStart.current.x;
    setDragOffset(dx);
  };

  const handleMouseUp = () => {
    if (!dragStart.current) return;
    handleTouchEnd();
  };

  const handleMouseLeave = () => {
    if (dragStart.current) handleTouchEnd();
  };

  const trackOffset = -(activeSlide * getCardStep()) + dragOffset;

  // Use active slide's period for the summary text (not just current time period)
  const activeSlideData = slides[activeSlide];
  const activePeriodKey = activeSlideData?.key || currentPeriod;
  const activePeriodTasks = todayCategorized?.[activePeriodKey] as DailyTask[] | undefined;
  const incompleteTasks = activePeriodTasks?.filter((task) => !task.completed) || [];
  const activePeriodLabel = t(TIME_PERIODS.find((p) => p.key === activePeriodKey)?.labelKey || 'routines.morning').toLowerCase();

  const summaryKey = incompleteTasks.length === 1 ? 'welcome.taskSummary_one' : 'welcome.taskSummary_other';
  const currentSummaryHtml = t(summaryKey, {
    count: String(incompleteTasks.length),
    period: activePeriodLabel,
  });
  const [displayedHtml, setDisplayedHtml] = useState('');
  const [summaryVisible, setSummaryVisible] = useState(false);
  const isFirstSummary = useRef(true);

  useEffect(() => {
    // First time with actual tasks: show immediately, no animation
    if (isFirstSummary.current && incompleteTasks.length > 0) {
      isFirstSummary.current = false;
      setDisplayedHtml(currentSummaryHtml);
      setSummaryVisible(true);
      return;
    }
    if (currentSummaryHtml === displayedHtml || !currentSummaryHtml) return;
    // Fade out old text
    setSummaryVisible(false);
    const timer = setTimeout(() => {
      // Swap text while invisible, then fade in
      setDisplayedHtml(currentSummaryHtml);
      setSummaryVisible(true);
    }, 400);
    return () => clearTimeout(timer);
  }, [activeSlide, incompleteTasks.length]);

  return (
    <Container>
      <LogoContainer>
        <svg width="36" height="37" viewBox="0 0 93 96" fill="none" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="shimmerGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="transparent" />
              <stop offset="50%" stopColor="rgba(255, 255, 255, 0.3)" />
              <stop offset="100%" stopColor="transparent" />
              <animateTransform
                attributeName="gradientTransform"
                type="translate"
                from="-1 0"
                to="1 0"
                dur="3s"
                repeatCount="indefinite"
              />
            </linearGradient>
            <mask id="logoMask">
              <path
                d="M46.6484 0.0187465C49.6024 0.259137 52.6836 3.1509 54.768 5.09665C63.5437 13.294 70.2788 23.7015 76.4993 33.8983C76.7114 33.788 76.4597 33.5575 76.4046 33.4288C75.0428 30.2062 72.8072 27.1758 71.2022 24.0762C71.1796 23.9504 71.2065 23.8443 71.2744 23.7368C71.4257 23.495 73.0037 22.5278 73.37 22.3002C75.904 20.7206 79.0036 19.2868 81.9025 18.5529C89.2188 16.7019 90.0079 19.6686 91.0656 26.1068C93.0113 37.9397 93.4554 55.5971 89.8735 67.0426C84.7871 83.3015 70.3637 92.6187 53.9153 94.6027C31.9549 97.2512 8.48424 88.986 2.22842 65.9764C-0.840096 54.6851 -0.349415 38.0938 1.36019 26.5042C1.86501 23.0765 2.29347 17.983 6.77747 17.9463C10.5601 17.9151 17.6686 21.0643 20.6692 23.3904C20.9379 23.5983 21.3876 23.9221 21.1741 24.2883C19.5012 27.272 17.4367 30.1256 15.9929 33.2323C15.9307 33.3652 15.7695 33.6678 15.9505 33.7555C21.9985 24.0819 28.2543 13.861 36.5534 5.94226C38.9036 3.70097 42.3157 0.290247 45.66 0.0187465C45.9541 -0.00529261 46.3543 -0.00529261 46.6484 0.0187465ZM57.8803 31.6994C55.4071 29.5501 49.3634 24.4764 46.1478 24.4368C43.5545 24.4043 39.7973 27.3356 37.7723 28.9024C25.6623 38.2748 8.61292 59.9821 19.7741 75.3898C32.546 93.0203 77.5075 89.2844 76.2929 62.7622C75.7781 51.5317 66.089 38.832 57.8803 31.6994Z"
                fill="white"
              />
              <path
                d="M61.2445 39.0635C66.823 45.1878 72.9106 54.1063 73.4635 62.6289C74.927 85.2172 37.9987 88.1414 24.9116 76.7469C9.38656 63.2299 27.8627 39.9756 39.7451 30.8761C43.5023 27.9985 45.4014 26.2479 49.8769 29.0477C53.9098 31.5718 58.0488 35.5538 61.246 39.0635H61.2445ZM37.706 50.6065C35.0942 48.1305 31.2706 49.9617 30.9765 53.446C30.811 55.3974 30.763 59.8602 31.0175 61.7437C31.6001 66.0495 37.4798 66.5048 38.7044 62.7293C39.236 61.0904 39.1809 55.7481 39.0239 53.8773C38.932 52.7757 38.5248 51.3828 37.706 50.6079V50.6065ZM56.6319 49.6025C55.3083 49.8274 53.9593 51.0831 53.6058 52.3628C53.2155 53.7726 53.2579 59.9832 53.4248 61.6376C53.8843 66.1796 60.3904 66.5628 61.2361 62.2542C61.5585 60.6139 61.5047 54.9477 61.3251 53.1702C61.089 50.8314 58.9707 49.2052 56.6319 49.6025Z"
                fill="white"
              />
            </mask>
            <clipPath id="clip0_11_98">
              <rect width="92.5519" height="95.076" fill="white" />
            </clipPath>
          </defs>
          <g clipPath="url(#clip0_11_98)">
            <path
              d="M46.6484 0.0187465C49.6024 0.259137 52.6836 3.1509 54.768 5.09665C63.5437 13.294 70.2788 23.7015 76.4993 33.8983C76.7114 33.788 76.4597 33.5575 76.4046 33.4288C75.0428 30.2062 72.8072 27.1758 71.2022 24.0762C71.1796 23.9504 71.2065 23.8443 71.2744 23.7368C71.4257 23.495 73.0037 22.5278 73.37 22.3002C75.904 20.7206 79.0036 19.2868 81.9025 18.5529C89.2188 16.7019 90.0079 19.6686 91.0656 26.1068C93.0113 37.9397 93.4554 55.5971 89.8735 67.0426C84.7871 83.3015 70.3637 92.6187 53.9153 94.6027C31.9549 97.2512 8.48424 88.986 2.22842 65.9764C-0.840096 54.6851 -0.349415 38.0938 1.36019 26.5042C1.86501 23.0765 2.29347 17.983 6.77747 17.9463C10.5601 17.9151 17.6686 21.0643 20.6692 23.3904C20.9379 23.5983 21.3876 23.9221 21.1741 24.2883C19.5012 27.272 17.4367 30.1256 15.9929 33.2323C15.9307 33.3652 15.7695 33.6678 15.9505 33.7555C21.9985 24.0819 28.2543 13.861 36.5534 5.94226C38.9036 3.70097 42.3157 0.290247 45.66 0.0187465C45.9541 -0.00529261 46.3543 -0.00529261 46.6484 0.0187465ZM57.8803 31.6994C55.4071 29.5501 49.3634 24.4764 46.1478 24.4368C43.5545 24.4043 39.7973 27.3356 37.7723 28.9024C25.6623 38.2748 8.61292 59.9821 19.7741 75.3898C32.546 93.0203 77.5075 89.2844 76.2929 62.7622C75.7781 51.5317 66.089 38.832 57.8803 31.6994Z"
              fill="#535353"
            />
            <path
              d="M61.2445 39.0635C66.823 45.1878 72.9106 54.1063 73.4635 62.6289C74.927 85.2172 37.9987 88.1414 24.9116 76.7469C9.38656 63.2299 27.8627 39.9756 39.7451 30.8761C43.5023 27.9985 45.4014 26.2479 49.8769 29.0477C53.9098 31.5718 58.0488 35.5538 61.246 39.0635H61.2445ZM37.706 50.6065C35.0942 48.1305 31.2706 49.9617 30.9765 53.446C30.811 55.3974 30.763 59.8602 31.0175 61.7437C31.6001 66.0495 37.4798 66.5048 38.7044 62.7293C39.236 61.0904 39.1809 55.7481 39.0239 53.8773C38.932 52.7757 38.5248 51.3828 37.706 50.6079V50.6065ZM56.6319 49.6025C55.3083 49.8274 53.9593 51.0831 53.6058 52.3628C53.2155 53.7726 53.2579 59.9832 53.4248 61.6376C53.8843 66.1796 60.3904 66.5628 61.2361 62.2542C61.5585 60.6139 61.5047 54.9477 61.3251 53.1702C61.089 50.8314 58.9707 49.2052 56.6319 49.6025Z"
              fill="#535353"
            />
            <rect
              x="0"
              y="0"
              width="92.5519"
              height="95.076"
              fill="url(#shimmerGradient)"
              mask="url(#logoMask)"
            />
          </g>
        </svg>
      </LogoContainer>

      <WelcomeText $isVisible={!isLoading}>
        <p>{t('welcome.greeting', { name: userName })}</p>
        <p>{t('welcome.subtitle')}</p>
      </WelcomeText>

      <AgentsSection>
        <AgentsSectionTitle>{lang === 'tr' ? 'Asistanlar' : 'Agents'}</AgentsSectionTitle>
        <AgentsScroll>
          {AGENT_OPTIONS.map((agent, idx) => {
            const [, right, , top] = agent.glowPosition.split(' ');
            return (
              <AgentCard
                key={agent.id}
                $index={idx}
                onClick={() => onAgentSelect?.(agent.id)}
              >
                <AgentCardGlow $color={agent.glowColor} $top={top} $right={right} />
                <AgentIcon src={agent.icon} alt={agent.label} />
                <AgentLabel>{lang === 'tr' ? agent.labelTr : agent.label}</AgentLabel>
              </AgentCard>
            );
          })}
        </AgentsScroll>
      </AgentsSection>

      {!routinesLoading && slides.length > 0 && incompleteTasks.length > 0 && (
        <TaskSummaryWrapper>
          <TaskSummaryLayer
            $active={summaryVisible}
            dangerouslySetInnerHTML={{ __html: displayedHtml }}
          />
        </TaskSummaryWrapper>
      )}

      <TaskSection>
        {routinesLoading ? (
          <CarouselWrapper style={{ pointerEvents: 'none' }}>
            <div style={{ padding: '0 14px' }}>
              <SkeletonCard>
                <SkeletonLine $width="40%" $height="12px" style={{ marginBottom: 20 }} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                  {[1, 2, 3].map((i) => (
                    <SkeletonRow key={i}>
                      <SkeletonBase style={{ width: 18, height: 18, borderRadius: 5, flexShrink: 0 }} />
                      <SkeletonLine $width={i === 2 ? '50%' : i === 3 ? '70%' : '80%'} />
                    </SkeletonRow>
                  ))}
                </div>
              </SkeletonCard>
            </div>
          </CarouselWrapper>
        ) : slides.length > 0 && (
        <CarouselWrapper
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
        >
          <CarouselTrack
            ref={trackRef}
            $offset={trackOffset}
            data-dragging={isDragging}
          >
            {slides.map((slide, slideIdx) => (
              <TaskCard key={slide.key}>
                <CardHeader>
                  {slide.emoji} {t(slide.labelKey).toUpperCase()} {t('routines.tasks').toUpperCase()}
                </CardHeader>
                <TaskList>
                  {slide.tasks.map((task, idx) => (
                    <TaskRow
                      key={`${animationKey}-${slide.key}-${task.id}`}
                      $completed={task.completed}
                      $index={idx}
                      $active={slideIdx === activeSlide}
                      $baseDelay={baseDelay}
                      onClick={() => setSelectedTask(task)}
                      style={{ cursor: 'pointer' }}
                    >
                      <Checkbox
                        $checked={task.completed}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!task.completed) {
                            trackTaskCompleted(buildWelcomeTaskProps(task));
                          }
                          onCompleteTask?.(today, task.id);
                        }}
                      >
                        {task.completed && (
                          <Check size={14} weight="bold" color="white" />
                        )}
                      </Checkbox>
                      <TaskInfo>
                        <TaskTitle $completed={task.completed}>{task.title}</TaskTitle>
                        {(task.contentTitle || task.contentType) && (
                          <TaskMeta>
                            {task.contentTitle && (
                              <ContentTitle>{task.contentTitle}</ContentTitle>
                            )}
                            {task.contentType && (
                              <ContentTypeBadge>
                                {t(CONTENT_TYPE_I18N_MAP[task.contentType.toLowerCase()] || '') || task.contentType}
                              </ContentTypeBadge>
                            )}
                          </TaskMeta>
                        )}
                      </TaskInfo>
                    </TaskRow>
                  ))}
                </TaskList>
              </TaskCard>
            ))}
          </CarouselTrack>
          {slides.length > 1 && (
            <DotsContainer>
              {slides.map((slide, idx) => (
                <Dot key={slide.key} $active={idx === activeSlide} />
              ))}
            </DotsContainer>
          )}
        </CarouselWrapper>
        )}
      </TaskSection>

      <TaskDetailSheet
        task={selectedTask}
        token={token}
        onClose={() => setSelectedTask(null)}
        onMarkDone={() => {
          if (selectedTask) {
            onCompleteTask?.(today, selectedTask.id);
            setSelectedTask(null);
          }
        }}
      />
    </Container>

  );
};
